// Device storage housekeeping for paperwork drafts.
//
// 2026-08-06, John Herring's iPhone: signing an expense sheet crashed the whole
// screen to the ChunkErrorBoundary. The logged error was Safari's
// "The quota has been exceeded." — browser storage was full, and the draft
// autosave in ExpenseReportEditor threw from inside a useEffect, which React
// treats as a lifecycle failure and unmounts the tree. Merrill hit the same
// wall on 2026-08-05. Twice is a pattern, so this fixes the cause, not the
// symptom.
//
// WHY THE DEVICE FILLS UP: every expense report is kept up to THREE times.
// localStorage holds `expenseReport_<id>` plus a `_prev` backup, and
// [[draftVault]] mirrors the live copy (not `_prev`) into IndexedDB so a
// half-finished report survives iOS wiping localStorage. That mirror is
// deliberate and worth keeping. What
// was missing is any cleanup: nothing ever removed a report after it was
// submitted, so a season of finished paperwork sat on the phone forever.
//
// Jill's ruling 2026-08-06: "Why can't slips and expense reports that are
// submitted automatically be removed from the device and just saved in the
// cloud? We can be honest with the user that after submitting, they will need
// an active internet connection to restore the draft from the database."
// The rule a pilot can actually hold in their head: if it is finished it lives
// in the cloud, if it is unfinished it lives on this device.
//
// Two facts make that rule safe, and both were verified before it shipped:
//   1. A report can only reach status "submitted" if the device had a working
//      connection at that moment, so we never drop a local copy that never
//      reached the server.
//   2. On a successful submit ExpenseReportEditor already deletes the cloud
//      `expense_drafts` row, so the sweep is permanent — unfinishedDrafts.js
//      cannot pull a submitted report back down onto the device afterwards.
//
// And the one thing this must never do: destroy a receipt that exists only
// here. Receipts are stashed locally with pendingUpload until they reach
// storage, and computeSyncSummary() counts exactly those for the "saved only
// on this device" warning. A report holding one is left completely alone, no
// matter its status.

const EXPENSE_PREFIX = "expenseReport_";

// A report is only droppable if it is finished AND nothing inside it is still
// waiting to reach the server. Anything unparseable is treated as "keep" —
// this routine deletes, so every uncertain case has to fail towards keeping.
function isDroppable(raw) {
  let rep;
  try {
    rep = JSON.parse(raw);
  } catch {
    return false; // unreadable: never delete what we cannot inspect
  }
  if (!rep || rep.status !== "submitted") return false;
  const receipts = Array.isArray(rep.receipts) ? rep.receipts : [];
  if (receipts.some((r) => r?.pendingUpload)) return false; // device-only, sacred
  return true;
}

/**
 * Remove the local copies of expense reports that are already submitted.
 *
 * Deletion goes through localStorage.removeItem, which draftVault has patched
 * to mirror the delete into IndexedDB — so one call clears both copies and
 * restoreDraftVault() will not resurrect them. MUST therefore run AFTER
 * installDraftVault(), or the vault keeps its copy and the space never frees.
 *
 * Returns { removed, freedBytes } for logging. Never throws: a device whose
 * storage is unreadable must still boot.
 */
export function sweepSubmittedExpenseDrafts() {
  let removed = 0;
  let freedBytes = 0;
  try {
    // Collect first, delete second. Removing while walking by index reindexes
    // localStorage underneath the loop and silently skips entries.
    const candidates = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(EXPENSE_PREFIX)) continue;
      if (key.endsWith("_prev")) continue; // handled with its parent below
      candidates.push(key);
    }

    for (const key of candidates) {
      let raw;
      try {
        raw = localStorage.getItem(key);
      } catch {
        continue;
      }
      if (!raw || !isDroppable(raw)) continue;

      const prevKey = `${key}_prev`;
      let prevSize = 0;
      try { prevSize = (localStorage.getItem(prevKey) || "").length; } catch { /* fine */ }

      try {
        localStorage.removeItem(key);
        localStorage.removeItem(prevKey); // the backup of a finished report is doubly pointless
        removed += 1;
        freedBytes += raw.length + prevSize;
      } catch { /* leave it; next boot tries again */ }
    }
  } catch { /* storage unavailable (private mode, locked) — nothing to do */ }

  if (removed) {
    console.log(
      `localDraftSpace: released ${removed} submitted expense report(s) from this device, about ${Math.round(freedBytes / 1024)} KB`
    );
  }
  return { removed, freedBytes };
}

// The `_prev` backup of FINISHED paperwork is shed below; the `_prev` of work
// in progress must survive. It is the one-step-back copy written when a cloud
// draft displaces a local one ("nothing is ever destroyed by the swap",
// PilotSlipEditor.jsx:457) — if that swap ever picks the wrong winner (a
// corrupted local timestamp, clock skew), the `_prev` holds the pilot's real
// work and is the ONLY copy anywhere: draftVault deliberately does not mirror
// `_prev` keys into IndexedDB (draftVault.js isVaulted). Once the paperwork is
// submitted, the account holds the finished record and the backup has nothing
// left to insure.
function liveCopyIsFinished(liveKey, raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false; // unreadable live copy: cannot prove finished, keep the backup
  }
  if (liveKey.startsWith(EXPENSE_PREFIX)) return parsed?.status === "submitted";
  const slip = parsed?.data || parsed;
  return (slip?.status || "draft") === "submitted";
}

/**
 * Release a FILED slip's local copy once the ACCOUNT confirms the filing.
 *
 * Jill's ruling 2026-08-07 (item 1b of the 8/6 list): "Isn't that why we have
 * a database?" — a filed slip's record is the account's `slip_drafts` row
 * (slip_state='filed', full envelope with signatures preserved by
 * finalizeSlipSubmit) plus the stored PDF. The device copy is an offline
 * convenience, and at ~195 KB apiece (signature images), held twice with its
 * `_prev`, six or seven of them fill Safari's budget — the storage-quota
 * incidents of 8/5 and 8/6 were exactly this weight.
 *
 * Called ONLY with a row the server just returned (seedCloudDrafts), never on
 * local knowledge alone — the account CONFIRMS, then the device releases. The
 * last copy can never go: we require the account row to carry the full
 * envelope before touching anything.
 *
 * Every uncertain case fails towards keeping:
 *  - no cloud envelope in the row: keep (the reopen-for-REVISED path adopts
 *    the account envelope; without one the local copy is the only source)
 *  - a submit still in flight on this device (pending / unsaved / queued
 *    markers): keep — the flight owns those keys
 *  - local envelope NEWER than the account row (2s grace, same convention as
 *    the cloud-displacement seed): keep — that is a revision in progress
 *  - unparseable local envelope: keep, never delete what we cannot inspect
 *
 * Only the envelope and its `_prev` go. The aux keys (slipPilotHints_ etc.)
 * stay: they are small and the expense editor's date hints read them
 * independently of the envelope. voyagePlanSlip_ caches stay for the same
 * reason.
 */
export function releaseFiledSlipCopy({ tripId, cloudTs, cloudHasEnvelope }) {
  const none = { removed: 0, freedBytes: 0 };
  if (!tripId || !cloudHasEnvelope) return none;
  try {
    const key = `pilotSlipDraft_${tripId}`;
    const raw = localStorage.getItem(key);
    if (raw == null) return none;
    let localTs = 0;
    try {
      localTs = Number(JSON.parse(raw)?.timestamp) || 0;
    } catch {
      return none; // unreadable: never delete what we cannot inspect
    }
    if (localTs > (Number(cloudTs) || 0) + 2000) return none; // newer local work
    return releaseSlipCopy(tripId, "filed slip lives in the account");
  } catch {
    return none; // storage unavailable — nothing to do
  }
}

/**
 * The one primitive that removes a slip's device copy (envelope + `_prev`).
 *
 * Jill's ruling 2026-08-12: "If a slip is submitted, let's completely delete
 * it off the device... finished paperwork needs a connection, full stop."
 * Every caller must hold SERVER-side proof the paperwork is safe before
 * calling — a confirmed filed-envelope upsert (finalizeSlipSubmit), a
 * server-returned filed row (releaseFiledSlipCopy), or a fresh
 * pilot_trips_v2 status read (the seedCloudDrafts reconcile sweep). The
 * device's own "status: submitted" is NEVER proof: the July RLS incident
 * taught us a client can believe a write happened that silently didn't.
 *
 * In-flight markers (a submit queued or pending on THIS device) always win:
 * the flight owns those keys and will clean up after itself.
 */
export function releaseSlipCopy(tripId, why = "released") {
  const none = { removed: 0, freedBytes: 0 };
  if (!tripId) return none;
  try {
    for (const marker of ["pilotSlipPending_", "pilotSlipUnsaved_", "submitQueue_slip_"]) {
      if (localStorage.getItem(`${marker}${tripId}`) != null) return none;
    }
    const key = `pilotSlipDraft_${tripId}`;
    const raw = localStorage.getItem(key);
    if (raw == null) return none;
    let prevSize = 0;
    try { prevSize = (localStorage.getItem(`${key}_prev`) || "").length; } catch { /* fine */ }
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_prev`);
    console.log(`localDraftSpace: slip ${tripId} — ${why}, released ~${Math.round((raw.length + prevSize) / 1024)} KB from this device`);
    return { removed: 1, freedBytes: raw.length + prevSize };
  } catch {
    return none; // storage unavailable — nothing to do
  }
}

/**
 * Release an ARCHIVED expense draft's local copy (live + `_prev`).
 *
 * Jill's ruling 2026-08-14 (the archive redesign): an archived trip's
 * paperwork lives in the account, not on the device — Archive frees the
 * space here, Restore pulls it back down. Same trust rules as
 * releaseSlipCopy: the CALLER must hold server-side proof the account's
 * expense_drafts mirror is at least as new as this copy (seedCloudDrafts
 * passes rows the server just returned); this function adds the local
 * guards.
 *
 * The one absolute: a receipt photo that never reached storage
 * (pendingUpload) exists ONLY on this device, and a report holding one is
 * never touched — same sacred rule as sweepSubmittedExpenseDrafts.
 */
export function releaseExpenseDraftCopy(reportId, why = "released") {
  const none = { removed: 0, freedBytes: 0 };
  if (!reportId) return none;
  try {
    const key = `${EXPENSE_PREFIX}${reportId}`;
    const raw = localStorage.getItem(key);
    if (raw == null) return none;
    let rep;
    try { rep = JSON.parse(raw); } catch { return none; } // never delete what we cannot inspect
    const receipts = Array.isArray(rep?.receipts) ? rep.receipts : [];
    if (receipts.some((r) => r?.pendingUpload)) return none; // device-only, sacred
    let prevSize = 0;
    try { prevSize = (localStorage.getItem(`${key}_prev`) || "").length; } catch { /* fine */ }
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_prev`);
    console.log(`localDraftSpace: expense ${reportId} — ${why}, released ~${Math.round((raw.length + prevSize) / 1024)} KB from this device`);
    return { removed: 1, freedBytes: raw.length + prevSize };
  } catch {
    return none; // storage unavailable — nothing to do
  }
}

/**
 * Shed the `_prev` backup copies of FINISHED paperwork only.
 *
 * NOTHING IN THE APP EVER READS a `_prev` BACK — there is no restore-from-prev
 * anywhere, they are a write-only safety net. Verified 2026-08-06 across every
 * reader: TripWorkspace, slipHandoff and the sweep above all explicitly skip
 * keys ending in `_prev`. But "unread by code" is not "worthless": see
 * liveCopyIsFinished above for why an in-progress draft's backup stays.
 * (2026-08-07: the first cut of this shed skipped that status check and
 * dropped in-progress backups too — caught in review the next morning, after
 * Jill lost offline slip edits to an iOS storage eviction the same night.
 * The eviction was unrelated, but it is exactly the kind of morning where a
 * safety net earns its weight.)
 *
 * On submitted SLIPS this is the biggest single win available without touching
 * anything a screen reads. A filed slip envelope averages 195 KB (it carries
 * the signature images) against 23 KB for a draft, and each one is held twice
 * in localStorage. Six or seven filed slips will fill Safari's budget on
 * their own.
 *
 * Only the `_prev` copy goes. The live `pilotSlipDraft_<id>` of a submitted
 * slip is deliberately KEPT: Jill ruled on 2026-07-25, after the VVE26-VOY007
 * iPad-to-Mac incident, that a filed slip stays readable on the device, and
 * four things still read it (the voyage plan, the expense editor's date hints,
 * the workspace list, and the handoff scanner).
 */
export function shedFinishedBackupCopies() {
  let removed = 0;
  let freedBytes = 0;
  try {
    const prevKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.endsWith("_prev")) continue;
      if (!key.startsWith("pilotSlipDraft_") && !key.startsWith(EXPENSE_PREFIX)) continue;
      prevKeys.push(key);
    }

    for (const key of prevKeys) {
      // Only shed the backup when the live copy is still there AND is
      // finished paperwork. A missing live copy means this `_prev` is the
      // last thing standing; an in-progress live copy means the backup is
      // still insuring the cloud-displacement swap. Either way it stays.
      const liveKey = key.slice(0, -"_prev".length);
      let liveRaw = null;
      try { liveRaw = localStorage.getItem(liveKey); } catch { /* treat as missing */ }
      if (liveRaw === null || !liveCopyIsFinished(liveKey, liveRaw)) continue;

      let size = 0;
      try { size = (localStorage.getItem(key) || "").length; } catch { /* fine */ }
      try {
        localStorage.removeItem(key);
        removed += 1;
        freedBytes += size;
      } catch { /* leave it; next boot tries again */ }
    }
  } catch { /* storage unavailable */ }

  if (removed) {
    console.log(
      `localDraftSpace: shed ${removed} unused backup copy(ies), about ${Math.round(freedBytes / 1024)} KB`
    );
  }
  return { removed, freedBytes };
}
